import React, { useContext, useEffect } from "react";
import { Form, Switch } from "antd";
import to from "await-to-js";
import dayjs from "dayjs";
import { t } from "i18next";

import style from "./AddAuthorizedPersonModal.module.scss";
import { Modal } from "../../../../../../components/Modal/Modal";
import { ModalsContext } from "../../../../../../context/modals.context";
import { Input } from "../../../../../../components/Input/Input";
import { DatePicker } from "../../../../../../components/DatePicker/DatePicker";
import { DATE_FORMAT } from "../../../../../../core/constants";
import { Button } from "../../../../../../components/Button/Button";
import { toastService } from "../../../../../../utils/toast.service";
import { IOvlascenaLicaPayload } from "../../../../../../modules/obligee/authorisedPersons/authorisedPersons.types";
import { authorisedPersonsService } from "../../../../../../modules/obligee/authorisedPersons/authorisedPersons.service";
import { MandatoryField } from "../../../../../../components/MandatoryField/MandatoryField";

interface IAddAuthorizedPersonModalProps {
  refetch: () => void;
}

export const AddAuthorizedPersonModal: React.FC<
  IAddAuthorizedPersonModalProps
> = ({ refetch }) => {
  const { isModalVisible, clearModals, modals, setError } =
    useContext(ModalsContext);

  const id = modals.id;
  const userInfo = modals.record;

  const [form] = Form.useForm<IOvlascenaLicaPayload>();

  const handleCancel = () => {
    clearModals();
    form.resetFields();
  };

  const onSubmit = async (values: IOvlascenaLicaPayload) => {
    const handleApi = () => {
      if (id) {
        return authorisedPersonsService.putIzmeniOvlascenoLiceZaPreuzimanjeMarkica(
          values,
          id
        );
      }
      return authorisedPersonsService.postKreirajOvlascenoLiceZaPreuzimanjeMarkica(
        values
      );
    };
    const [err, res] = await to(handleApi());
    if (err || !res.success) return setError(res?.errorMessage);
    if (id) {
      refetch();
      clearModals();
      return toastService.success("TOAST.IZMENILI_OVLASCENO");
    }
    toastService.success("TOAST.DODALI_OVLASCENO");
    refetch();
    form.resetFields();
    clearModals();
  };

  useEffect(() => {
    form.setFieldsValue({
      ime: userInfo ? userInfo.ime : "",
      prezime: userInfo ? userInfo.prezime : "",
      aktivno: userInfo ? userInfo.aktivno : false,
      brojLicneKarte: userInfo ? userInfo.brojLicneKarte : "",
      datumVazenjaOvlascenjaOd: userInfo.datumVazenjaOvlascenjaOd
        ? dayjs(userInfo.datumVazenjaOvlascenjaOd)
        : null,
      datumVazenjaOvlascenjaDo: userInfo.datumVazenjaOvlascenjaDo
        ? dayjs(userInfo.datumVazenjaOvlascenjaDo)
        : null,
    });
  }, [userInfo]);

  return (
    <Modal
      name="dodaj-ovlasceno-lice-modal"
      title={
        id
          ? t("AKCIZNE_MARKICE.IZMENI_OVLASCENO_LICE")
          : t("AKCIZNE_MARKICE.DODAJ_OVLASCENO_LICE")
      }
      footer={false}
      onCancel={handleCancel}
      open={isModalVisible("dodajOvlasceno")}
    >
      <div>
        <Form
          form={form}
          labelCol={{ span: 12 }}
          labelAlign="left"
          wrapperCol={{ span: 12 }}
          onFinish={(values: IOvlascenaLicaPayload) => onSubmit(values)}
        >
          <div className={style.form}>
            <Form.Item
              name="ime"
              colon={false}
              label={MandatoryField("AKCIZNE_MARKICE.IME")}
            >
              <Input
                placeholder={t("AKCIZNE_MARKICE.IME")}
                name="dodaj-ovlasceno-ime"
              />
            </Form.Item>
            <Form.Item
              name="prezime"
              colon={false}
              label={MandatoryField("AKCIZNE_MARKICE.PREZIME")}
            >
              <Input
                placeholder={t("AKCIZNE_MARKICE.PREZIME")}
                name="dodaj-ovlasceno-prezime"
              />
            </Form.Item>
            <Form.Item
              name="brojLicneKarte"
              colon={false}
              label={MandatoryField("AKCIZNE_MARKICE.BROJ_LICNE_KARTE")}
            >
              <Input
                placeholder={t("AKCIZNE_MARKICE.BROJ_LICNE_KARTE")}
                name="dodaj-ovlasceno-brlk"
              />
            </Form.Item>
            <Form.Item
              name="datumVazenjaOvlascenjaOd"
              colon={false}
              label={t("AKCIZNE_MARKICE.DATUM_VAZENJA_OVLASCENJA_OD")}
            >
              <DatePicker
                showToday={false}
                placeholder={t("PLACEHOLDERS.IZABERITE_DATUM_OD")}
                format={DATE_FORMAT}
                name="datum-vazenja-ovlascenja-od"
              />
            </Form.Item>
            <Form.Item
              name="datumVazenjaOvlascenjaDo"
              colon={false}
              label={t("AKCIZNE_MARKICE.DATUM_VAZENJA_OVLASCENJA_DO")}
            >
              <DatePicker
                showToday={false}
                placeholder={t("PLACEHOLDERS.IZABERITE_DATUM_DO")}
                format={DATE_FORMAT}
                name="datum-vazenja-ovlascenja-do"
              />
            </Form.Item>
            <Form.Item
              name="aktivno"
              colon={false}
              label={t("AKCIZNE_MARKICE.AKTIVNO")}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </div>
          <div className={style.footer}>
            <div>
              <Button type="default" onClick={handleCancel}>
                {t("AKCIZNE_MARKICE.NAZAD")}
              </Button>
            </div>
            <Form.Item className="ml-20">
              <Button htmlType="submit" type="primary">
                {id ? t("AKCIZNE_MARKICE.IZMENI") : t("AKCIZNE_MARKICE.DODAJ")}
              </Button>
            </Form.Item>
          </div>
        </Form>
      </div>
    </Modal>
  );
};
